import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin-guard";
import { z } from "zod";

const eventSchema = z.object({
  event_name: z.string().min(1).max(60),
  page_path: z.string().max(255).optional(),
  device: z.enum(["mobile", "tablet", "desktop"]).optional(),
  location: z.string().max(60).optional(),
  service: z.string().max(60).optional(),
  metadata: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  user_agent: z.string().max(500).optional(),
  referrer: z.string().max(500).optional(),
  session_id: z.string().max(64).optional(),
});

function getAdminClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !key) throw new Error("Server configuration error");
  return import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(url, key, { auth: { persistSession: false, autoRefreshToken: false } })
  );
}

/** Public — record a single analytics event from the front-end. */
export const recordEvent = createServerFn({ method: "POST" })
  .validator((input: unknown) => eventSchema.parse(input))
  .handler(async ({ data }) => {
    const supabase = await getAdminClient();
    const { error } = await supabase.from("analytics_events").insert({
      event_name: data.event_name,
      page_path: data.page_path || null,
      device: data.device || null,
      location: data.location || null,
      service: data.service || null,
      metadata: data.metadata || null,
      user_agent: data.user_agent || null,
      referrer: data.referrer || null,
      session_id: data.session_id || null,
    });
    if (error) {
      console.error("Analytics insert failed:", error);
      // Never throw to the front-end — analytics must not break UX
      return { success: false };
    }
    return { success: true };
  });

const adminSchema = z.object({
  password: z.string().max(200).optional(),
  range: z.enum(["24h", "7d", "30d", "90d", "all"]).default("7d"),
});

function rangeToDate(range: "24h" | "7d" | "30d" | "90d" | "all"): Date | null {
  const now = Date.now();
  const map: Record<string, number> = {
    "24h": 24 * 60 * 60 * 1000,
    "7d": 7 * 24 * 60 * 60 * 1000,
    "30d": 30 * 24 * 60 * 60 * 1000,
    "90d": 90 * 24 * 60 * 60 * 1000,
  };
  if (range === "all") return null;
  return new Date(now - map[range]);
}

/** Admin — fetch analytics dashboard summary. */
export const getAnalyticsDashboard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => adminSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const since = rangeToDate(data.range);

    let query = supabase
      .from("analytics_events")
      .select("event_name, page_path, device, location, service, created_at, metadata")
      .order("created_at", { ascending: false })
      .limit(5000);
    if (since) query = query.gte("created_at", since.toISOString());

    const { data: events, error } = await query;
    if (error) throw new Error("Failed to load events");

    let leadsQuery = supabase
      .from("leads")
      .select("id, type, service, name, phone, vehicle_reg, created_at, urgency")
      .order("created_at", { ascending: false })
      .limit(500);
    if (since) leadsQuery = leadsQuery.gte("created_at", since.toISOString());
    const { data: leads, error: leadsError } = await leadsQuery;
    if (leadsError) throw new Error("Failed to load leads");

    return {
      events: events || [],
      leads: leads || [],
    };
  });

/** Admin — verify the caller is an active admin (used for the login gate). */
export const verifyAdmin = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    try {
      await assertAdmin(context.userId);
      return { ok: true };
    } catch {
      return { ok: false };
    }
  });

const configSchema = z.object({
  password: z.string().max(200).optional(),
  key: z.string().min(1).max(64),
  value: z.unknown(),
});

/** Admin — write a single site_config key. */
export const saveSiteConfig = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((input: unknown) => configSchema.parse(input))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId);
    const supabase = await getAdminClient();
    const { error } = await supabase
      .from("site_config")
      .upsert({ key: data.key, value: data.value as never, updated_at: new Date().toISOString() });
    if (error) {
      console.error("site_config upsert failed:", error);
      throw new Error("Failed to save");
    }
    return { success: true };
  });

type Json = string | number | boolean | null | { [k: string]: Json } | Json[];

export interface SiteConfigResponse {
  config: Record<string, Json>;
}

/** Public — read all site_config rows. */
export const getSiteConfig = createServerFn({ method: "GET" })
  .handler(async (): Promise<SiteConfigResponse> => {
    const supabase = await getAdminClient();
    const { data, error } = await supabase.from("site_config").select("key, value");
    if (error) {
      console.error("site_config read failed:", error);
      return { config: {} };
    }
    const config: Record<string, Json> = {};
    for (const row of data || []) {
      config[(row as { key: string }).key] = (row as { value: Json }).value;
    }
    return { config };
  });
